import { useCallback } from 'react';
import { crewEvaluationsService } from '../services';
import {
  useResourceCollection,
  useResourceById,
  useCreateResource,
  useUpdateResource,
  useDeleteResource,
  ResourceState,
  SingleResourceState,
  MutationState,
} from './useResource';
import { ResourceQueryOptions } from '../services/resources.service';
import { CrewEvaluation } from '../types';

export function useCrewEvaluations(
  options?: ResourceQueryOptions
): ResourceState<CrewEvaluation> & { meta?: any } {
  const fetchFn = useCallback(
    (opts?: ResourceQueryOptions) =>
      crewEvaluationsService.getAll(opts) as unknown as Promise<{ data: CrewEvaluation[]; meta?: any }>,
    []
  );
  return useResourceCollection<CrewEvaluation>(fetchFn, options);
}

export function useCrewEvaluation(
  id: string | null,
  options?: ResourceQueryOptions
): SingleResourceState<CrewEvaluation> {
  const resourceId = id;
  const fetchFn = useCallback(
    (evaluationId: string | number, opts?: ResourceQueryOptions) =>
      crewEvaluationsService.getById(evaluationId, opts) as unknown as Promise<CrewEvaluation>,
    []
  );
  return useResourceById<CrewEvaluation>(fetchFn, resourceId, options);
}

export function useCreateCrewEvaluation(
  onSuccess?: (data: CrewEvaluation) => void
): MutationState<CrewEvaluation> {
  const createFn = useCallback(
    (data: any) => crewEvaluationsService.create(data) as unknown as Promise<CrewEvaluation>,
    []
  );
  return useCreateResource<CrewEvaluation>(createFn, onSuccess);
}

export function useUpdateCrewEvaluation(
  onSuccess?: (data: CrewEvaluation) => void
): MutationState<CrewEvaluation> {
  const updateFn = useCallback(
    (id: string | number, data: any) => {
      return crewEvaluationsService.update(id, data) as unknown as Promise<CrewEvaluation>;
    },
    []
  );
  return useUpdateResource<CrewEvaluation>(updateFn, onSuccess);
}

/**
 * Delete crew evaluation using documentId
 * @param data - Full crew evaluation object containing documentId
 */
export function useDeleteCrewEvaluation(onSuccess?: () => void): MutationState<void> {
  const deleteFn = useCallback(
    (data: any) => {
      const documentId = data.documentId || data.id?.toString() || data.id;
      return crewEvaluationsService.delete(documentId) as unknown as Promise<void>;
    },
    []
  );
  return useDeleteResource(deleteFn, onSuccess);
}

